import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { ArrowLeft, ArrowRight, Calendar, Tag, ExternalLink, Mail, Check } from 'lucide-react';
import PillarLayout from './pillar/PillarLayout';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;
const BASE_URL = 'https://enbet-consulting.de';

function formatGermanDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' });
  } catch { return ''; }
}

function renderBlock(text) {
  if (!text) return null;
  // Einfaches Absatz-Splitting; behalte sanft Zeilenumbrüche
  const paras = text.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);
  return paras.map((p, i) => (
    <p key={i} className="text-[#1f1f1f]/75 text-base md:text-lg leading-relaxed mb-4 last:mb-0" style={{ fontFamily: "'Manrope', sans-serif" }}>
      {p.split('\n').map((line, j, arr) => (
        <React.Fragment key={j}>
          {line}
          {j < arr.length - 1 && <br />}
        </React.Fragment>
      ))}
    </p>
  ));
}

export default function NewsletterArticle() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/public/newsletter-issues/${slug}`)
      .then((r) => {
        if (cancelled) return;
        if (r.data && r.data.slug) {
          setArticle(r.data);
        } else {
          setNotFound(true);
        }
      })
      .catch(() => { if (!cancelled) setNotFound(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug]);

  if (loading) {
    return (
      <PillarLayout title="Lade Insight... | ENBET Consulting" description="ENBET Insights" canonical={`${BASE_URL}/insights/${slug}`}>
        <section className="bg-white pt-32 pb-20 min-h-[60vh]">
          <div className="max-w-[900px] mx-auto px-6 md:px-10 text-center text-gray-400">Lade…</div>
        </section>
      </PillarLayout>
    );
  }

  if (notFound || !article) {
    return (
      <PillarLayout title="Insight nicht gefunden | ENBET Consulting" description="Diese Insights-Ausgabe ist nicht verfügbar." canonical={`${BASE_URL}/insights/${slug}`}>
        <section className="bg-white pt-32 pb-20 min-h-[60vh]">
          <div className="max-w-[800px] mx-auto px-6 md:px-10 text-center">
            <h1 className="text-[#1f1f1f] text-3xl md:text-4xl font-bold uppercase mb-4" style={{ fontFamily: "'Oswald', sans-serif" }}>
              Diese Ausgabe gibt es nicht (mehr).
            </h1>
            <p className="text-[#1f1f1f]/60 text-base md:text-lg mb-8" style={{ fontFamily: "'Manrope', sans-serif" }}>
              Möglich, dass sie noch nicht veröffentlicht ist oder der Link nicht stimmt. Schau in der Insights-Übersicht.
            </p>
            <Link to="/#blog" className="inline-flex items-center gap-2 bg-[#0a0a0a] text-[#befa0a] px-6 py-3 text-xs uppercase tracking-wider font-bold hover:bg-[#befa0a] hover:text-[#0a0a0a]" style={{ fontFamily: "'Oswald', sans-serif" }}>
              <ArrowLeft size={14} /> Zurück zur Übersicht
            </Link>
          </div>
        </section>
      </PillarLayout>
    );
  }

  const canonical = `${BASE_URL}/insights/${article.slug}`;
  const isoDate = article.published_at || article.created_at;
  const dateLabel = formatGermanDate(isoDate);

  const schemaLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        '@id': `${canonical}#article`,
        headline: article.headline,
        description: article.summary || (article.intro || '').slice(0, 200),
        author: { '@id': `${BASE_URL}/#sebastian-funke` },
        publisher: { '@id': `${BASE_URL}/#organization` },
        mainEntityOfPage: canonical,
        datePublished: isoDate,
        inLanguage: 'de-DE',
        articleSection: article.category || 'Insight',
        image: article.image_url ? [article.image_url] : undefined,
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Start', item: BASE_URL + '/' },
          { '@type': 'ListItem', position: 2, name: 'Insights', item: BASE_URL + '/#blog' },
          { '@type': 'ListItem', position: 3, name: article.headline, item: canonical },
        ],
      },
    ],
  };

  const sources = Array.isArray(article.sources) ? article.sources : [];

  return (
    <PillarLayout
      title={`${article.headline} | ENBET Consulting`}
      description={article.summary || (article.intro || '').slice(0, 160)}
      canonical={canonical}
      schemaLd={schemaLd}
    >
      {/* Hero */}
      <section className="bg-white pt-32 pb-16 md:pt-40 md:pb-20">
        <div className="max-w-[840px] mx-auto px-6 md:px-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="flex items-center gap-4 mb-6 text-xs">
            <span className="text-[#e07a2f] uppercase tracking-[0.25em] font-bold flex items-center gap-1.5" style={{ fontFamily: "'Oswald', sans-serif" }}>
              <Tag size={12} /> {article.category || 'Insight'}
            </span>
            {dateLabel && (
              <span className="text-[#1f1f1f]/45 flex items-center gap-1.5" style={{ fontFamily: "'Manrope', sans-serif" }}>
                <Calendar size={12} /> {dateLabel}
              </span>
            )}
          </motion.div>

          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.05 }}
            className="text-[#1f1f1f] text-3xl md:text-5xl lg:text-6xl font-bold uppercase tracking-tight leading-[1.1] mb-6"
            style={{ fontFamily: "'Oswald', sans-serif" }}>
            {article.headline}
          </motion.h1>

          {article.summary && (
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.15 }}
              className="text-[#1f1f1f]/65 text-base md:text-xl leading-relaxed border-l-2 border-[#befa0a] pl-5 max-w-2xl"
              style={{ fontFamily: "'Manrope', sans-serif" }}>
              {article.summary}
            </motion.p>
          )}
        </div>
      </section>

      {article.image_url ? (
        <section className="bg-white pb-12">
          <div className="max-w-[1100px] mx-auto px-6 md:px-10">
            <div className="aspect-[16/9] overflow-hidden bg-[#0a0a0a]">
              <img src={article.image_url} alt={article.headline} className="w-full h-full object-cover" />
            </div>
          </div>
        </section>
      ) : (
        // Fallback: dunkler Akzent-Streifen statt fehlender 16:9-Lücke.
        // Hält den Übergang vom Hero in den Body sauber und visuell konsistent
        // mit Insights die ein Bild haben.
        <section className="bg-white pb-8">
          <div className="max-w-[1100px] mx-auto px-6 md:px-10">
            <div className="bg-[#0a0a0a] relative overflow-hidden py-12 md:py-16 px-8 md:px-14">
              <div className="absolute top-0 left-0 w-full h-1 bg-[#befa0a]" />
              <div className="absolute inset-0 opacity-[0.05]" style={{ backgroundImage: 'radial-gradient(circle at 15% 50%, #befa0a 0%, transparent 60%), radial-gradient(circle at 85% 50%, #befa0a 0%, transparent 60%)' }} />
              <div className="relative flex items-center gap-5">
                <div className="w-1 h-12 bg-[#befa0a] flex-shrink-0" />
                <div>
                  <p className="text-[#befa0a] text-[10px] uppercase tracking-[0.4em] font-bold mb-2" style={{ fontFamily: "'Oswald', sans-serif" }}>
                    ENBET Insights
                  </p>
                  <p className="text-white/70 text-sm md:text-base leading-snug max-w-2xl" style={{ fontFamily: "'Manrope', sans-serif" }}>
                    {article.category ? `${article.category}.` : 'Insight.'} Praxisnaher Beitrag für stationären Handel und serviceintensive Betriebe.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Body */}
      <section className="bg-white pb-16 md:pb-24">
        <div className="max-w-[800px] mx-auto px-6 md:px-10 space-y-10 md:space-y-12">
          {article.intro && (
            <article>
              {renderBlock(article.intro)}
            </article>
          )}

          {article.usecase && (
            <article>
              <h2 className="text-[#1f1f1f] text-xl md:text-2xl font-bold uppercase tracking-tight leading-tight mb-4" style={{ fontFamily: "'Oswald', sans-serif" }}>
                Der Use-Case
              </h2>
              {renderBlock(article.usecase)}
            </article>
          )}

          {article.technology && (
            <article>
              <h2 className="text-[#1f1f1f] text-xl md:text-2xl font-bold uppercase tracking-tight leading-tight mb-4" style={{ fontFamily: "'Oswald', sans-serif" }}>
                Die Technologie
              </h2>
              {renderBlock(article.technology)}
            </article>
          )}

          {/* Sources */}
          {sources.length > 0 && (
            <article className="pt-6 border-t border-[#e5e5e5]">
              <h3 className="text-[#1f1f1f]/60 text-xs uppercase tracking-[0.25em] font-bold mb-4" style={{ fontFamily: "'Oswald', sans-serif" }}>
                Quellen
              </h3>
              <ul className="space-y-2">
                {sources.map((src, i) => (
                  <li key={i}>
                    <a href={src} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-[#658a07] hover:text-[#1f1f1f] text-sm break-all"
                      style={{ fontFamily: "'Manrope', sans-serif" }}>
                      <ExternalLink size={12} className="flex-shrink-0" /> {src}
                    </a>
                  </li>
                ))}
              </ul>
            </article>
          )}
        </div>
      </section>

      {/* CTA-Block */}
      <section className="bg-[#0a0a0a] py-16 md:py-20">
        <div className="max-w-[800px] mx-auto px-6 md:px-10 text-center">
          <p className="text-[#befa0a] text-xs uppercase tracking-[0.25em] font-bold mb-4" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Dein nächster Schritt
          </p>
          <h2 className="text-white text-2xl md:text-4xl font-bold uppercase tracking-tight leading-tight mb-6" style={{ fontFamily: "'Oswald', sans-serif" }}>
            {article.cta_text || 'Lass uns über deinen Betrieb sprechen.'}
          </h2>
          <p className="text-white/60 text-base md:text-lg mb-8 max-w-xl mx-auto" style={{ fontFamily: "'Manrope', sans-serif" }}>
            20 Minuten, kostenlos, ehrliche Einschätzung — kein Verkaufsdruck.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/#booking"
              className="inline-flex items-center justify-center gap-2 bg-[#befa0a] text-[#0a0a0a] px-8 py-4 text-sm font-bold uppercase tracking-[0.15em] hover:bg-white transition-colors"
              style={{ fontFamily: "'Oswald', sans-serif" }}>
              <Check size={16} /> Potenzial-Check buchen
            </Link>
            <Link to="/#newsletter-section"
              className="inline-flex items-center justify-center gap-2 border border-white/30 text-white px-8 py-4 text-sm font-bold uppercase tracking-[0.15em] hover:bg-white hover:text-[#0a0a0a] transition-colors"
              style={{ fontFamily: "'Oswald', sans-serif" }}>
              <Mail size={16} /> Newsletter abonnieren
            </Link>
          </div>
        </div>
      </section>

      {/* Weiterführende Pillars */}
      <section className="py-12 bg-white">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10">
          <p className="text-[#1f1f1f]/50 text-xs uppercase tracking-[0.25em] font-bold mb-5" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Weiter vertiefen
          </p>
          <div className="flex flex-wrap gap-3">
            {[
              { to: '/ki-im-stationaeren-handel', label: 'KI im stationären Handel' },
              { to: '/ki-ready-check', label: 'KI Ready Check (Modul 1)' },
              { to: '/ki-strategie-workshop', label: 'KI Strategie Workshop (Modul 2)' },
              { to: '/ki-masterclass', label: 'KI-Masterclass' },
            ].map((l, i) => (
              <Link key={i} to={l.to}
                className="inline-flex items-center gap-2 border border-[#e5e5e5] text-[#1f1f1f]/70 hover:border-[#befa0a] hover:text-[#1f1f1f] px-4 py-2 text-xs uppercase tracking-wider transition-colors"
                style={{ fontFamily: "'Oswald', sans-serif" }}>
                {l.label} <ArrowRight size={12} />
              </Link>
            ))}
          </div>
        </div>
      </section>
    </PillarLayout>
  );
}
